import {
  HttpInterceptorFn,
  HttpHandlerFn,
  HttpRequest,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, BehaviorSubject } from 'rxjs';
import { catchError, filter, take, switchMap, finalize, timeout } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { environment } from '../../../../environments/environment';

const REFRESH_FAILED = '__REFRESH_FAILED__';

class TokenRefreshManager {
  private static isRefreshing = false;
  private static readonly refreshTokenSubject = new BehaviorSubject<string | null>(null);

  static resetState(): void {
    this.isRefreshing = false;
    // Signal failure to waiting requests before resetting
    this.refreshTokenSubject.next(REFRESH_FAILED);
    this.refreshTokenSubject.next(null);
  }

  static get isCurrentlyRefreshing(): boolean {
    return this.isRefreshing;
  }

  static set isCurrentlyRefreshing(value: boolean) {
    this.isRefreshing = value;
  }

  static get tokenSubject(): BehaviorSubject<string | null> {
    return this.refreshTokenSubject;
  }
}

function handleLogoutAndRedirect(authService: AuthService, router: Router): void {
  authService.logout().subscribe({
    next: (response) => {
      if (response?.logoutUrl) {
        globalThis.location.href = response.logoutUrl;
      } else {
        router.navigate(['/login']);
      }
    },
    error: () => {
      router.navigate(['/login']);
    },
  });
}

function waitForTokenRefresh(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<any>> {
  return TokenRefreshManager.tokenSubject.pipe(
    filter((token): token is string => token !== null),
    take(1),
    timeout(15000), // 15s timeout to prevent hanging if refresh fails silently
    switchMap((token) => {
      // If refresh failed, don't retry — redirect to login
      if (token === REFRESH_FAILED) {
        handleLogoutAndRedirect(authService, router);
        return throwError(() => ({ message: 'Token refresh failed' }));
      }
      const retryReq = addToken(request, authService, token);
      return next(retryReq);
    }),
    catchError(() => {
      // Timeout or error waiting for refresh — redirect to login
      handleLogoutAndRedirect(authService, router);
      return throwError(() => ({ message: 'Token refresh timeout' }));
    })
  );
}

function performTokenRefresh(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<any>> {
  TokenRefreshManager.isCurrentlyRefreshing = true;
  TokenRefreshManager.tokenSubject.next(null);

  return authService.refreshToken().pipe(
    switchMap((tokenResponse) => {
      const newToken = tokenResponse?.accessToken || authService.getToken();
      TokenRefreshManager.tokenSubject.next(newToken);
      const retryReq = addToken(request, authService, newToken ?? undefined);
      return next(retryReq);
    }),
    catchError((err) => {
      TokenRefreshManager.resetState();
      handleLogoutAndRedirect(authService, router);
      return throwError(() => err);
    }),
    finalize(() => {
      TokenRefreshManager.isCurrentlyRefreshing = false;
    })
  );
}

/**
 * Optimized auth interceptor with smart token refresh strategy.
 * - Uses cached expiration checks for performance
 * - Implements grace period for recently expired tokens
 * - Triggers background refresh instead of blocking all requests
 */
export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);
  const router = inject(Router);

  if (isAuthEndpoint(req.url)) {
    return next(req);
  }

  const token = authService.getToken();
  const refreshToken = authService.getRefreshToken();

  // Use cached expiration info for performance
  const expirationInfo = authService.getTokenExpirationInfo();

  // Token valid - proceed immediately
  if (token && !expirationInfo.isExpired) {
    return next(addToken(req, authService)).pipe(
      catchError((error) => handleHttpError(error, req, next, authService, router))
    );
  }

  // Token expired but within grace period - try with existing token, refresh on 401
  if (token && expirationInfo.isExpired && expirationInfo.gracePeriod && refreshToken) {
    return next(addToken(req, authService)).pipe(
      catchError((error) => handleHttpError(error, req, next, authService, router))
    );
  }

  // Token critically expired or no token - must refresh first
  if (refreshToken && !TokenRefreshManager.isCurrentlyRefreshing) {
    return performTokenRefresh(req, next, authService, router);
  }

  // If refresh is already in progress, wait for it
  if (refreshToken && TokenRefreshManager.isCurrentlyRefreshing) {
    return waitForTokenRefresh(req, next, authService, router);
  }

  // No token and no refresh token - proceed without auth (will likely fail with 401)
  return next(req).pipe(
    catchError((error) => handleHttpError(error, req, next, authService, router))
  );
};

/**
 * Unified HTTP error handler for the interceptor
 */
function handleHttpError(
  error: any,
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<any>> {
  let status: number | undefined;

  if (error instanceof HttpErrorResponse) {
    status = error.status;
  } else if (error && typeof error === 'object') {
    if ('statusCode' in error) {
      status = error.statusCode;
    } else if ('status' in error) {
      status = error.status;
    }
  }

  if (status) {
    switch (status) {
      case 401:
        return handle401Error(request, next, authService, router);

      case 403:
        router.navigate(['/error/access-denied']);
        return throwError(() => error);

      case 0:
        return throwError(() => ({
          ...error,
          message: 'Network error. Please check your connection.',
        }));

      default:
        return throwError(() => error);
    }
  }

  return throwError(() => error);
}

function addToken(request: HttpRequest<any>, authService: AuthService, directToken?: string): HttpRequest<any> {
  const token = directToken || authService.getToken();

  if (token) {
    return request.clone({
      setHeaders: {
        Authorization: `Bearer ${token}`,
        'X-Content-Type-Options': 'nosniff',
        'X-Frame-Options': 'DENY',
        'X-XSS-Protection': '1; mode=block',
      },
    });
  }

  return request;
}

function handle401Error(
  request: HttpRequest<any>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<any>> {
  if (isRefreshEndpoint(request.url)) {
    TokenRefreshManager.resetState();
    handleLogoutAndRedirect(authService, router);
    return throwError(() => ({ message: 'Refresh token expired' }));
  }

  // If no refresh token available, redirect to login immediately
  if (!authService.getRefreshToken()) {
    handleLogoutAndRedirect(authService, router);
    return throwError(() => ({ message: 'No refresh token available' }));
  }

  if (!TokenRefreshManager.isCurrentlyRefreshing) {
    return performTokenRefresh(request, next, authService, router);
  }

  return waitForTokenRefresh(request, next, authService, router);
}

function isAuthEndpoint(url: string): boolean {
  const authEndpoints = [
    '/auth/login',
    '/auth/logout',
    '/auth/callback',
    '/auth/refresh',
    '/auth/register',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/api/auth/login',
    '/api/auth/register',
    '/api/auth/forgot-password',
    '/api/auth/reset-password',
    '/api/public',
    '/public',
    'api/SystemUser/UserVerification',
    // Kiosk-public endpoints carry their own X-Kiosk-Token, never a JWT.
    // Treat them like /api/public so the auth interceptor doesn't add a
    // JWT header, react to 401s with a login redirect, or attempt token
    // refresh on the kiosk's behalf.
    '/api/KioskPublic/',
  ];

  if (environment.authConfig) {
    if (environment.authConfig.loginUrl) authEndpoints.push(environment.authConfig.loginUrl);
    if (environment.authConfig.logoutUrl) authEndpoints.push(environment.authConfig.logoutUrl);
    if (environment.authConfig.callbackUrl) authEndpoints.push(environment.authConfig.callbackUrl);
    if (environment.authConfig.refreshUrl) authEndpoints.push(environment.authConfig.refreshUrl);
  }

  return authEndpoints.some((endpoint) => endpoint && url.includes(endpoint));
}

function isRefreshEndpoint(url: string): boolean {
  const refreshEndpoints = [
    '/auth/refresh',
    '/api/auth/refresh',
    environment.authConfig?.refreshUrl || '',
  ].filter(Boolean);

  return refreshEndpoints.some((endpoint) => url.includes(endpoint));
}
